import { hasStimulus } from '../Stimulus/stimulusHelpers';
import { normalizeFileUrls } from '../../utils/normalizeFileUrls';
import {
  sortBySerialNumber,
  getQuestionMarks,
  formatQuestionLabel,
} from './submissionViewUtils';

export const getParentSerial = (question) =>
  question?.parentQuestionSerialNumber ?? question?.parentSerialNumber ?? null;

export const isTopLevelQuestion = (question) => {
  const parent = getParentSerial(question);
  return parent == null || parent === '';
};

export const getParentQuestion = (allQuestions, child) => {
  const parentSn = getParentSerial(child);
  if (parentSn == null || parentSn === '') return null;
  return (allQuestions || []).find(
    (q) => String(q.serialNumber) === String(parentSn),
  );
};

export const getAnswerableQuestions = (allQuestions) => {
  const questions = allQuestions || [];
  const parentsWithChildren = new Set();

  questions.forEach((q) => {
    const parent = getParentSerial(q);
    if (parent != null && parent !== '') {
      parentsWithChildren.add(String(parent));
    }
  });

  questions.forEach((q) => {
    if (Array.isArray(q.children) && q.children.length > 0) {
      parentsWithChildren.add(String(q.serialNumber));
    }
  });

  const answerable = questions.filter((q) => {
    const parent = getParentSerial(q);
    const sn = String(q.serialNumber);

    if (parent != null && parent !== '') return true;
    return !parentsWithChildren.has(sn);
  });

  return sortBySerialNumber(answerable);
};

/**
 * The answerable questions of a submission, read straight from the question list the
 * API sent — the same array the document view resolves ?question= against.
 */
export const getSubmissionAnswerableQuestions = (submission) =>
  getAnswerableQuestions(submission?.assignment?.questions || []);

// The three shapes the API uses for sub-parts. getAnswerableQuestions understands flat
// parent refs and `children`; a question carrying any of these is multi-part whatever
// shape it arrived in.
const NESTED_PART_KEYS = ['children', 'subQuestions', 'multipartChildren'];

const hasSubParts = (questions) =>
  questions.some((question) =>
    NESTED_PART_KEYS.some(
      (key) => Array.isArray(question?.[key]) && question[key].length > 0,
    ),
  );

/**
 * Does this submission skip the question-index screen entirely?
 *
 * THE single source of truth for that decision. Four places depend on it and they must
 * agree exactly: SubmissionViewWrapper (which redirects past the index), the header
 * back-arrow, and the two "View all questions" buttons (which are hidden). If any of them
 * disagreed, that control would navigate to a screen that immediately redirects back.
 *
 * Three deliberate narrowings:
 *
 * SUBMISSION only — a My Drafts portfolio DOCUMENT is single-question by construction but
 * lives on its own /documents/ route, and the review chrome is wrong for one.
 *
 * No sub-parts — one question with three parts is three answers and genuinely needs the
 * index. Checked structurally rather than by normalising, so it holds for every shape the
 * API sends and this module stays free of the task-authoring helpers (which pull in a
 * drag-and-drop library that has no business in a util the mobile app imports).
 *
 * ANSWERABLE questions, not raw rows — a question that is only a heading for others is
 * not something anyone answers. The serial is then re-checked against the raw list,
 * because that is what the destination resolves ?question= against: if we ever hand it a
 * serial it cannot find, it silently opens the first question instead.
 */
export const skipsQuestionIndex = (submission) => {
  if (submission?.type !== 'SUBMISSION') {
    return false;
  }
  const questions = submission.assignment?.questions || [];
  if (hasSubParts(questions)) {
    return false;
  }
  const answerable = getAnswerableQuestions(questions);
  return (
    answerable.length === 1 &&
    questions.some(
      (q) => String(q?.serialNumber) === String(answerable[0]?.serialNumber),
    )
  );
};

export const getAnswerableSerialNumber = (allQuestions, serialNumber) => {
  const answerable = getAnswerableQuestions(allQuestions);
  if (!answerable.length) return serialNumber || '';

  if (serialNumber) {
    const match = answerable.find(
      (q) => String(q.serialNumber) === String(serialNumber),
    );
    if (match) return match.serialNumber;

    const children = answerable.filter(
      (q) => String(getParentSerial(q)) === String(serialNumber),
    );
    if (children.length) return children[0].serialNumber;
  }

  return answerable[0].serialNumber;
};

export const resolveSubmissionQuestionFromUrl = (allQuestions, serialFromUrl) => {
  const questions = allQuestions || [];
  const urlSerial = serialFromUrl || '';
  const correctSerial = getAnswerableSerialNumber(questions, urlSerial);
  const index = questions.findIndex(
    (q) => String(q.serialNumber) === String(correctSerial),
  );

  return {
    serial: correctSerial,
    index: index >= 0 ? index : 0,
    needsUrlUpdate: Boolean(
      correctSerial && String(correctSerial) !== String(urlSerial),
    ),
  };
};

export const formatNavLabel = formatQuestionLabel;

export const getStimulusSourceQuestion = (question, allQuestions) => {
  if (!question) return null;
  if (hasStimulus(question)) return question;
  return getParentQuestion(allQuestions, question) || question;
};

export const getAnswerForQuestion = (submission, serialNumber) => {
  const sn = String(serialNumber);
  return submission?.answers?.find(
    (a) =>
      String(a.serialNumber) === sn ||
      String(a.questionSerialNumber) === sn,
  );
};

/**
 * Patch one answer into the shared submission without replacing other answers
 * with a stale response snapshot. The API uses both serialNumber and
 * questionSerialNumber on older records, and the answer payload itself is
 * nested under answer.
 */
export const updateSubmissionAnswer = (submission, serialNumber, patch) => {
  if (!submission) return submission;

  const answers = Array.isArray(submission.answers)
    ? submission.answers
    : [];
  const sn = String(serialNumber);
  const index = answers.findIndex(
    (answer) =>
      String(answer?.serialNumber) === sn ||
      String(answer?.questionSerialNumber) === sn,
  );
  const current = index >= 0 ? answers[index] : { serialNumber };
  const currentValue =
    current?.answer &&
    typeof current.answer === 'object' &&
    !Array.isArray(current.answer)
      ? current.answer
      : { answer: typeof current?.answer === 'string' ? current.answer : '' };
  const nextAnswer = {
    ...current,
    answer: { ...currentValue, ...(patch || {}) },
  };
  const nextAnswers = answers.slice();

  if (index >= 0) nextAnswers[index] = nextAnswer;
  else nextAnswers.push(nextAnswer);

  return { ...submission, answers: nextAnswers };
};

/**
 * Replace one cached answer with the canonical row returned by saveAnswer.
 * Other answers may have changed concurrently in this tab, so the server's
 * whole submission snapshot must not replace them.
 */
export const replaceSubmissionAnswer = (
  submission,
  serialNumber,
  savedAnswer
) => {
  if (!submission || !savedAnswer) return submission;
  const answers = Array.isArray(submission.answers) ? submission.answers : [];
  const sn = String(serialNumber);
  const index = answers.findIndex(
    (answer) =>
      String(answer?.serialNumber) === sn ||
      String(answer?.questionSerialNumber) === sn
  );
  const current = index >= 0 ? answers[index] : { serialNumber };
  const nextAnswers = answers.slice();
  const canonical = {
    ...current,
    ...savedAnswer,
    // Older/backend-transition responses can omit fields they did not change.
    // Merge the inner DTO so an answer-only canonical response cannot silently
    // discard locally held comment anchors; an explicit `highlights: []` still
    // clears them as intended.
    answer:
      current?.answer &&
      savedAnswer?.answer &&
      typeof current.answer === 'object' &&
      typeof savedAnswer.answer === 'object' &&
      !Array.isArray(current.answer) &&
      !Array.isArray(savedAnswer.answer)
        ? { ...current.answer, ...savedAnswer.answer }
        : savedAnswer.answer,
  };

  if (index >= 0) nextAnswers[index] = canonical;
  else nextAnswers.push(canonical);

  return { ...submission, answers: nextAnswers };
};

export const canonicalSavedAnswer = (response, serialNumber) => {
  if (
    !response ||
    typeof response !== 'object' ||
    Array.isArray(response) ||
    !Array.isArray(response.answers)
  ) {
    return null;
  }
  const saved = getAnswerForQuestion(response, serialNumber);
  return saved && saved.answer != null ? saved : null;
};

export const canonicalAttachedAnswer = (
  response,
  serialNumber,
  expectedUrls
) => {
  const saved = canonicalSavedAnswer(response, serialNumber);
  const returnedUrls = saved?.answer?.fileUrls;
  if (!Array.isArray(returnedUrls) || !Array.isArray(expectedUrls)) return null;
  return JSON.stringify(returnedUrls) === JSON.stringify(expectedUrls)
    ? saved
    : null;
};

/**
 * Apply the semantic contract of PATCH /files. An exact ordered-list retry is
 * an idempotent no-op and preserves OCR/text. A changed list clears them; make
 * those clears explicit locally during the UI-first rollout because an older
 * NON_EMPTY backend can omit fields it cleared.
 */
export const replaceSubmissionAttachmentAnswer = (
  submission,
  serialNumber,
  savedAnswer,
  fileUrls
) => {
  const currentAnswer = getAnswerForQuestion(submission, serialNumber);
  const currentUrls = Array.isArray(currentAnswer?.answer?.fileUrls)
    ? currentAnswer.answer.fileUrls
    : [];
  const requestedUrls = Array.isArray(fileUrls) ? fileUrls : [];
  const replaced = replaceSubmissionAnswer(
    submission,
    serialNumber,
    savedAnswer
  );
  if (JSON.stringify(currentUrls) === JSON.stringify(requestedUrls)) {
    return replaced;
  }
  return updateSubmissionAnswer(replaced, serialNumber, {
    answer: '',
    fileUrls: requestedUrls,
    textExtractedAt: null,
    extractionStatus: null,
    extractionFailureReason: null,
  });
};

// True when an answer has any content — typed text OR a handwritten upload.
// Handwritten responses live in answer.fileUrls (no text), so checking text
// alone reports handwritten-only work as empty. Mirrors
// hasRenderableSubmissionAnswers in FeedbacksRoot/functions.jsx.
export const answerHasContent = (answer) => {
  if (!answer) return false;
  const raw = answer?.answer?.answer ?? answer?.answer ?? '';
  const text = typeof raw === 'string' ? raw.replace(/<[^>]*>/g, '').trim() : '';
  const hasFiles = normalizeFileUrls(answer?.answer?.fileUrls).length > 0;
  return text.length > 0 || hasFiles;
};

export const hasAnswerContent = (submission, serialNumber) =>
  answerHasContent(getAnswerForQuestion(submission, serialNumber));

export const getCompletionPercent = (submission, answerableQuestions) => {
  if (!answerableQuestions?.length) return 0;
  const answered = answerableQuestions.filter((q) =>
    hasAnswerContent(submission, q.serialNumber),
  ).length;
  return Math.round((answered / answerableQuestions.length) * 100);
};

export { getQuestionMarks };
